import React, { useMemo, useState } from 'react';
import { Image, Rect, Text } from 'react-konva';
import useImage from 'use-image';
import { resolvePlaceholderUrl } from '../../../services/ArtworkResolver';

const initials = (value) => String(value || '?')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0] || '')
  .join('')
  .toUpperCase();

const badgeSlot = (slotIndex, slotCount) => {
  if (slotCount <= 1) return { x: 0.52, y: 0.52, size: 0.46 };
  if (slotCount === 2) {
    return [
      { x: 0.52, y: 0.02, size: 0.46 },
      { x: 0.52, y: 0.52, size: 0.46 },
    ][slotIndex] || { x: 0.52, y: 0.52, size: 0.46 };
  }
  if (slotCount === 3) {
    return [
      { x: 0.52, y: 0.02, size: 0.46 },
      { x: 0.02, y: 0.52, size: 0.46 },
      { x: 0.52, y: 0.52, size: 0.46 },
    ][slotIndex] || { x: 0.52, y: 0.52, size: 0.46 };
  }

  const columns = Math.ceil(Math.sqrt(slotCount));
  const size = Math.max(0.2, Math.min(0.44, (1 / columns) - 0.04));
  const row = Math.floor(slotIndex / columns);
  const column = slotIndex % columns;
  return {
    x: 0.02 + column * (size + 0.04),
    y: 0.02 + row * (size + 0.04),
    size,
  };
};

const CoLocatedTokenBadge = ({
  token,
  cell,
  slotIndex = 0,
  slotCount = 1,
  gridSize = 50,
  targetingEnabled = false,
  isTargetSelected = false,
  onActivate,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [hovered, setHovered] = useState(false);
  const source = useMemo(() => {
    const explicit = [token?.src, token?.tokenVisual?.url]
      .find((value) => typeof value === 'string' && value.trim());
    return explicit?.trim() || resolvePlaceholderUrl('token-default');
  }, [token]);
  const [image] = useImage(source);
  const pixelsPerCell = Number(gridSize) > 0 ? Number(gridSize) : 50;
  const slot = badgeSlot(slotIndex, slotCount);
  const size = pixelsPerCell * slot.size;
  const x = cell.x * pixelsPerCell + pixelsPerCell * slot.x;
  const y = cell.y * pixelsPerCell + pixelsPerCell * slot.y;
  const name = String(token?.name || 'Occupant');
  const border = isTargetSelected ? '#f5c84c' : '#eef3fa';

  const activate = (event) => {
    event.cancelBubble = true;
    try {
      event?.evt?.preventDefault?.();
      event?.evt?.stopPropagation?.();
    } catch (_) {}
    onActivate?.(token.id, event);
  };
  const enter = () => {
    setHovered(true);
    onMouseEnter?.(token.id);
  };
  const leave = () => {
    setHovered(false);
    onMouseLeave?.();
  };
  const interactiveProps = {
    onClick: activate,
    onTap: activate,
    onMouseEnter: enter,
    onMouseLeave: leave,
  };
  const tooltipWidth = Math.max(72, Math.min(180, name.length * 6.2 + 14));
  const tooltipY = y >= 24 ? y - 22 : y + size + 4;

  return (
    <React.Fragment>
      <Rect
        x={x - 2}
        y={y - 2}
        width={size + 4}
        height={size + 4}
        fill="rgba(8, 13, 22, .92)"
        stroke={border}
        strokeWidth={targetingEnabled ? 2.5 : 1.5}
        cornerRadius={4}
        shadowColor="#000000"
        shadowBlur={4}
        {...interactiveProps}
      />
      {image && (
        <Image
          image={image}
          x={x}
          y={y}
          width={size}
          height={size}
          {...interactiveProps}
        />
      )}
      <Text
        x={x}
        y={y + Math.max(1, size * 0.28)}
        width={size}
        height={size * 0.5}
        text={initials(name)}
        fontSize={Math.max(7, Math.min(12, size * 0.34))}
        fontStyle="bold"
        fill="#ffffff"
        stroke="#0b111b"
        strokeWidth={0.6}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
      {hovered && (
        <React.Fragment>
          <Rect
            x={x + size - tooltipWidth}
            y={tooltipY}
            width={tooltipWidth}
            height={19}
            fill="rgba(8, 13, 22, .97)"
            stroke="#d9b867"
            strokeWidth={1}
            cornerRadius={3}
            listening={false}
          />
          <Text
            x={x + size - tooltipWidth + 5}
            y={tooltipY + 4}
            width={tooltipWidth - 10}
            height={13}
            text={name}
            fontSize={10}
            fill="#f3ead7"
            ellipsis
            listening={false}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default React.memo(CoLocatedTokenBadge);
